import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  signIn,
  signUp,
  signOut,
  signInWithGoogle,
  getSession,
  getProfile,
  onAuthStateChange,
} from '../services/auth';
import type { User, Session } from '@supabase/supabase-js';
import type { Profile } from '../types/database.types';
import { invalidateCacheKey } from '../services/apiCache';
import { invalidateMarketplaceCache } from '../../features/bookings/utils/marketplaceCache';
import { apiClient } from '../services/api';
import {
  buildUserCacheFromProfileAndOnboarding,
  writeUserCache,
} from '../utils/syncUserProfileCache';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, role?: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string) => {
    const p = await getProfile(userId);
    if (!p) return;

    let onboarding = null;
    try {
      const onboardingResult = await apiClient.getOnboarding();
      if (onboardingResult.success && onboardingResult.data) {
        onboarding = onboardingResult.data;
      }
    } catch {
      /* onboarding row may not exist yet */
    }

    if (!p.location) {
      const derivedLocation = [onboarding?.city, onboarding?.state, onboarding?.country]
        .filter(Boolean)
        .join(', ');
      if (derivedLocation) {
        p.location = derivedLocation;
        try {
          await apiClient.updateUserProfile({ location: derivedLocation } as any);
        } catch (e) {
          console.warn('Auto-syncing location to profile failed', e);
        }
      }
    }

    setProfile(p);
    writeUserCache(buildUserCacheFromProfileAndOnboarding(p, onboarding));
  }, []);

  useEffect(() => {
    // Get initial session
    const initSession = async () => {
      try {
        const currentSession = await getSession();

        setSession(currentSession);
        setUser(currentSession?.user || null);

        if (currentSession?.user) {
          localStorage.setItem('access_token', currentSession.access_token);
          localStorage.setItem('refresh_token', currentSession.refresh_token);
          invalidateCacheKey('referrers');
          invalidateMarketplaceCache();
          void fetchProfile(currentSession.user.id);
        }
      } catch (error) {
        console.error('Failed to initialize session:', error);
        setSession(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initSession();

    // Listen for auth changes.
    // IMPORTANT: Supabase calls this callback while holding the internal auth
    // lock (processLock).  Any `await` here blocks ALL other auth operations
    // (getSession, onAuthStateChange in other components, etc.) until it
    // resolves.  So we must NOT await network calls like fetchProfile — fire
    // them without awaiting so the lock is released immediately.
    const { data: { subscription } } = onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user || null);

      if (newSession?.user) {
        localStorage.setItem('access_token', newSession.access_token);
        localStorage.setItem('refresh_token', newSession.refresh_token);
        invalidateCacheKey('referrers');
        invalidateMarketplaceCache();
        void fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const handleSignIn = async (email: string, password: string) => {
    await signIn(email, password);
  };

  const handleSignUp = async (email: string, password: string, fullName: string, role?: string) => {
    await signUp(email, password, fullName, role);
  };

  const handleSignOut = async () => {
    await signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
  };

  const handleSignInWithGoogle = async () => {
    await signInWithGoogle({ intent: 'signup' });
  };

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        signIn: handleSignIn,
        signUp: handleSignUp,
        signOut: handleSignOut,
        signInWithGoogle: handleSignInWithGoogle,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return ctx;
}

export default AuthContext;
